import { RoseTree } from '@tableau/data-structures';
import { NestedItemBasicInfo, NestedItemsTreeHelper } from '@tableau/nested-project-explorer';
import { useEffect, useState } from 'react';

import { DestinationProjects } from '../../projects/resources/DestinationProjects';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { DestinationProjectMapper } from '../../ts/mappers/DestinationProjectMapper';

export function useProjects() {
  const [projects, setProjects] = useState<RoseTree<NestedItemBasicInfo>[]>(null);
  useEffect(() => {
    getAllProjectsAsTrees().then(projectTree => {
      setProjects(projectTree);
    });
  }, []); // No dependencies, so empty array to prevent re-renders
  return projects;
}

export function getSiteName(): string {
  return getSessionManagementServiceInstance().sessionSiteName();
}

function getInitialProject(tree: RoseTree<NestedItemBasicInfo>[], projectId: string) {
  if (projectId) {
    return getProjectById(tree, projectId);
  } else {
    return getDefaultProject(tree);
  }
}

function getDefaultProject(projectTree: RoseTree<NestedItemBasicInfo>[]): NestedItemBasicInfo {
  if (!projectTree) {
    return null;
  }
  const lowestProject = projectTree.reduce((lowestProject: RoseTree<NestedItemBasicInfo>, project: RoseTree<NestedItemBasicInfo>) =>
    Number(lowestProject.node.id) > Number(project.node.id) && project.node.nestingLevel === 1 ? project : lowestProject
  , projectTree[0]);

  return lowestProject?.node;
}

function getProjectById(tree: RoseTree<NestedItemBasicInfo>[], projectId: string): NestedItemBasicInfo {
  if (!tree) {
    return null;
  }
  let retProjectNode: NestedItemBasicInfo;
  const projNode = tree.find(projectNode => {
    if (projectNode.node.id === projectId) {
      return true;
    } else if (projectNode.node.hasChildren) {
      projectNode.forEach(p => {
        if (p.id === projectId) {
          retProjectNode = p;
        }});
      return false;
    }
  });
  return retProjectNode ? retProjectNode : projNode?.node ?? null;
}

function getAllProjectsAsTrees(): Promise<RoseTree<NestedItemBasicInfo>[]> {
  // Returns all projects as trees
  const contentType = 'project';
  const itemIds: string[] = [];
  const query = '';
  const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(contentType, itemIds, query);
  return SliceableHelper.fetchAll(projectsQuery).then(projects => {
    const destinationProjects = DestinationProjectMapper.map(projects.items);
    const nestedProjects = NestedItemsTreeHelper.convertItemListToTrees(destinationProjects);
    return nestedProjects;
  });
}

export default {
  getInitialProject,
};
